// src/App.jsx
import React, { useContext } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Home from './pages/Home';
import Analysis from './pages/Analysis';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Logout from './pages/Logout';
import Login from './pages/Login';
import Signup from './pages/Signup';
import VerifyEmail from './pages/VerifyEmail';
import Footer from './components/Footer';
import Banner from './components/Banner';
import BannerStack from './components/BannerStack';
import BetaToast from './components/BetaToast';
import { ThemeContext, ThemeProvider } from './context/ThemeContext';
import { NavigationProvider } from './context/NavigationContext';
import { WarningsProvider } from './context/WarningsContext';
import { SidebarProvider, useSidebar } from './context/SidebarContext';
import { AuthContext } from './context/AuthContext';

function AppContent() {
  const { isDarkMode } = useContext(ThemeContext);
  const { isExpanded } = useSidebar();
  const { user, loading } = useContext(AuthContext);
  const location = useLocation();
  
  // Define routes where footer should not appear
  const isLogout = location.pathname === '/logout';
  const isAuthPage = location.pathname === '/login' || location.pathname === '/signup' || location.pathname === '/verify-email';

  if (loading) {
    return <div className="h-screen w-screen flex items-center justify-center dark:bg-gray-900 dark:text-white">Loading...</div>;
  }

  if (!user?.authenticated && !isAuthPage) {
    return <Navigate to="/login" replace />;
  }

  if (isAuthPage) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'dark' : ''}`}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
        </Routes>
      </div>
    );
  }

  // Determine banner content based on current route
  const getBannerContent = () => {
    if (location.pathname === '/') {
      return {
        title: "SleepScorer",
        subtitle: "Sleep Analysis Platform"
      };
    } else if (location.pathname === '/profile') {
      return {
        title: "Profile",
        subtitle: "Manage your account settings and preferences"
      };
    } else if (location.pathname === '/settings') {
      return {
        title: "Settings",
        subtitle: "Configure your application preferences"
      };
    }
    return { title: "", subtitle: "" };
  };

  const bannerContent = getBannerContent();

  return (
    <div className={`flex h-screen ${isDarkMode ? 'dark' : ''}`}>
      <Sidebar />
      <div className="flex-1 flex flex-col">
        {/* Sticky header section with dynamic margin */}
        <div className={`sticky top-0 z-50 bg-white dark:bg-gray-900 transition-all duration-300 ${isExpanded ? 'ml-[140px]' : 'ml-12'}`}>
          {location.pathname.startsWith('/analysis') ? (
            <Analysis renderBanner={(patientInfo) => (
              <>
                <div className="border-b border-gray-200 dark:border-gray-700">
                  <Banner 
                    title="Sleep Study Analysis"
                    subtitle={patientInfo ? `Patient: ${patientInfo.name} (ID: ${patientInfo.id})${patientInfo.recordingDate ? ` - ${new Date(patientInfo.recordingDate).toLocaleDateString()}` : ''}` : 'Loading patient information...'}
                  />
                </div>
                <BannerStack />
              </>
            )} />
          ) : (
            <div className="border-b border-gray-200 dark:border-gray-700">
              <Banner title={bannerContent.title} subtitle={bannerContent.subtitle} />
            </div>
          )}
        </div>
        
        {/* Main content with fixed margin */}
        <main className="flex-1 overflow-y-auto ml-12 bg-white dark:bg-gray-900">
          <div className="min-h-full">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/analysis/*" element={<Analysis />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/logout" element={<Logout />} />
            </Routes>
          </div>
          {!isLogout && <Footer />}
        </main>
        <BetaToast />
      </div>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <SidebarProvider>
        <NavigationProvider>
          <WarningsProvider>
            <AppContent />
          </WarningsProvider>
        </NavigationProvider>
      </SidebarProvider>
    </ThemeProvider>
  );
}

export default App;
